import { createSlice } from '@reduxjs/toolkit';
import { axiosInstance } from '../Axios';
import { toggleSubcontractorModal } from 'reducers/UiReducer';

const getAxiosConfig = (token) => ({
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    Authorization: `Bearer ${token}`,
  },
});

const getErrorMessage = (error) =>
  error.response && error.response.data.message ? error.response.data.message : error.message;

const SubcontractorReducer = createSlice({
  name: 'subcontractor',
  initialState: {
    subcontractorLoading: false,
    subcontractorList: [],
    subcontractorError: null,
    subcontractorSuccessMessage: '',
  },
  reducers: {
    createSubcontractorRequest(state) {
      state.subcontractorLoading = true;
      state.subcontractorError = null;
      state.subcontractorSuccessMessage = '';
    },
    createSubcontractorSuccess(state, action) {
      state.subcontractorLoading = false;
      state.subcontractorSuccessMessage = action.payload || 'Subcontractor saved successfully';
    },
    createSubcontractorFail(state, action) {
      state.subcontractorLoading = false;
      state.subcontractorError = action.payload;
    },
    updateSubcontractorRequest(state) {
      state.subcontractorLoading = true;
      state.subcontractorError = null;
      state.subcontractorSuccessMessage = '';
    },
    updateSubcontractorSuccess(state, action) {
      state.subcontractorLoading = false;
      state.subcontractorSuccessMessage = action.payload || 'Subcontractor updated successfully';
    },
    updateSubcontractorFail(state, action) {
      state.subcontractorLoading = false;
      state.subcontractorError = action.payload;
    },
    getSubcontractorListRequest(state) {
      state.subcontractorLoading = true;
    },
    getSubcontractorListSuccess(state, action) {
      state.subcontractorLoading = false;
      state.subcontractorList = action.payload;
    },
    getSubcontractorListFail(state, action) {
      state.subcontractorLoading = false;
      state.subcontractorError = action.payload;
    },
    deleteSubcontractorRequest(state) {
      state.subcontractorLoading = true;
    },
    deleteSubcontractorSuccess(state) {
      state.subcontractorLoading = false;
    },
    deleteSubcontractorFail(state, action) {
      state.subcontractorLoading = false;
      state.subcontractorError = action.payload;
    },
  },
});

export const {
  createSubcontractorRequest,
  createSubcontractorSuccess,
  createSubcontractorFail,
  updateSubcontractorRequest,
  updateSubcontractorSuccess,
  updateSubcontractorFail,
  getSubcontractorListRequest,
  getSubcontractorListSuccess,
  getSubcontractorListFail,
  deleteSubcontractorRequest,
  deleteSubcontractorSuccess,
  deleteSubcontractorFail,
} = SubcontractorReducer.actions;

export const createSubcontractorDispatch = (subcontractorData, token) => async (dispatch) => {
  try {
    dispatch(createSubcontractorRequest());
    const { data } = await axiosInstance.post('/Subcontractor/CreateSubcontractor', subcontractorData, getAxiosConfig(token));

    if (data.status === 1) {
      dispatch(createSubcontractorSuccess(data.message || 'Subcontractor created successfully'));
      dispatch(getSubcontractorListDispatch({ Offset: 0, RecordCount: 20, SubcontractorName: '' }, token));
      dispatch(toggleSubcontractorModal(false));
    } else {
      dispatch(createSubcontractorFail(data.message || 'Unable to create subcontractor'));
    }

    return data;
  } catch (error) {
    dispatch(createSubcontractorFail(getErrorMessage(error)));
    return {
      status: 0,
      message: getErrorMessage(error),
    };
  }
};

export const updateSubcontractorDispatch = (subcontractorData, token) => async (dispatch) => {
  try {
    dispatch(updateSubcontractorRequest());
    const { data } = await axiosInstance.post('/Subcontractor/UpdateSubcontractor', subcontractorData, getAxiosConfig(token));

    if (data.status === 1) {
      dispatch(updateSubcontractorSuccess(data.message || 'Subcontractor updated successfully'));
      dispatch(getSubcontractorListDispatch({ Offset: 0, RecordCount: 20, SubcontractorName: '' }, token));
      dispatch(toggleSubcontractorModal(false));
    } else {
      dispatch(updateSubcontractorFail(data.message || 'Unable to update subcontractor'));
    }

    return data;
  } catch (error) {
    dispatch(updateSubcontractorFail(getErrorMessage(error)));
    return {
      status: 0,
      message: getErrorMessage(error),
    };
  }
};

export const getSubcontractorListDispatch = (form, token) => async (dispatch) => {
  try {
    dispatch(getSubcontractorListRequest());
    const subcontractorDto = form?.subcontractorDto || {};
    const subcontractorName =
      form?.SubcontractorName ?? form?.subcontractorName ?? subcontractorDto.SubcontractorName ?? subcontractorDto.subcontractorName ?? '';
    const offset = form?.Offset ?? form?.offset ?? subcontractorDto.offset ?? 0;
    const recordCount =
      form?.RecordCount ?? form?.recordCount ?? subcontractorDto.recordCount ?? subcontractorDto.pageSize ?? 20;

    const { data } = await axiosInstance.get('/Subcontractor/GetSubcontractorList', {
      ...getAxiosConfig(token),
      params: {
        Offset: offset,
        RecordCount: recordCount,
        SubcontractorName: subcontractorName,
      },
    });

    dispatch(getSubcontractorListSuccess(data));
  } catch (error) {
    dispatch(getSubcontractorListFail(getErrorMessage(error)));
  }
};

export const deleteSubcontractorDispatch = (subcontractorId, token) => async (dispatch) => {
  try {
    dispatch(deleteSubcontractorRequest());
    await axiosInstance.delete(`/Subcontractor/DeleteSubcontractorById/${subcontractorId}`, getAxiosConfig(token));
    dispatch(deleteSubcontractorSuccess());
  } catch (error) {
    dispatch(deleteSubcontractorFail(getErrorMessage(error)));
  }
};

export default SubcontractorReducer;
